import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import {
  Plus, Handshake, ArrowUpCircle, ArrowDownCircle, CheckCircle2,
  Ban, Trash2, Mail, XCircle, PhoneCall, Radio, Megaphone, Coins, Stethoscope, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { InvestorPicker } from '../../components/InvestorPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  open: { label: 'Open', color: 'bg-emerald-100 text-emerald-700' },
  closed_to_new_allocations: { label: 'Closed to new allocations', color: 'bg-amber-100 text-amber-700' },
  live: { label: 'Live', color: 'bg-indigo-100 text-indigo-700' },
  concluded: { label: 'Concluded', color: 'bg-blue-100 text-blue-700' },
  written_off: { label: 'Written off', color: 'bg-red-100 text-red-700' },
};

const ALLOCATION_TYPE_LABEL = {
  new_investment: 'New investment',
  fund_reallocation: 'Fund reallocation',
};

const DISTRIBUTION_TYPE_LABEL = {
  withdrawal: 'Withdrawal',
  reinvestment: 'Reinvestment',
};

const formatCurrency = (v) => `R ${(Number(v) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const EMPTY_NEW_DEAL = {
  name: '', description: '', counterparty: '', deal_type: '', target_amount: '',
  expected_collection_amount: '', expected_conclusion_date: '', expected_settlement_date: '',
  indicative_return_low: '', indicative_return_high: '', indicative_return_note: '',
};

const EMPTY_ALLOCATION = { investor_id: '', allocation_type: 'new_investment', amount: '', allocation_date: '', notes: '', capital_call_id: '', funds_via_investor_account: null };
const EMPTY_CALL = { investor_id: '', amount: '', due_date: '', notes: '' };
const EMPTY_COLLECTION = { amount: '', collection_date: '', notes: '' };
const EMPTY_UPDATE_MSG = '';

export default function AdminDealRoomPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [investors, setInvestors] = useState([]);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newDeal, setNewDeal] = useState(EMPTY_NEW_DEAL);
  const [creating, setCreating] = useState(false);

  const [selectedDealId, setSelectedDealId] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [debtLeg, setDebtLeg] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [eois, setEois] = useState([]);
  const [capitalCalls, setCapitalCalls] = useState([]);
  const [collections, setCollections] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showCollectionDialog, setShowCollectionDialog] = useState(false);
  const [collectionForm, setCollectionForm] = useState(EMPTY_COLLECTION);
  const [collecting, setCollecting] = useState(false);

  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateMsg, setUpdateMsg] = useState(EMPTY_UPDATE_MSG);
  const [posting, setPosting] = useState(false);

  const [goLiveConfirmOpen, setGoLiveConfirmOpen] = useState(false);
  const [goingLive, setGoingLive] = useState(false);

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_NEW_DEAL);
  const [editing, setEditing] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showDiagnosticDialog, setShowDiagnosticDialog] = useState(false);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [fixingSigns, setFixingSigns] = useState(false);
  const [legacyLegs, setLegacyLegs] = useState(null);
  const [fixingLegacyLegs, setFixingLegacyLegs] = useState(false);

  const [showAllocateDialog, setShowAllocateDialog] = useState(false);
  const [allocForm, setAllocForm] = useState(EMPTY_ALLOCATION);
  const [allocating, setAllocating] = useState(false);

  const [showCallDialog, setShowCallDialog] = useState(false);
  const [callForm, setCallForm] = useState(EMPTY_CALL);
  const [calling, setCalling] = useState(false);

  const [showDistributeDialog, setShowDistributeDialog] = useState(false);
  const [distributeDate, setDistributeDate] = useState('');
  const [distributeRows, setDistributeRows] = useState([]);
  const [distributeLoading, setDistributeLoading] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [distributeResults, setDistributeResults] = useState(null);

  const [concludeConfirmOpen, setConcludeConfirmOpen] = useState(false);
  const [concluding, setConcluding] = useState(false);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/deal-room/deals`, { headers });
      setDeals(res.data.deals || []);
    } catch (error) {
      toast.error('Failed to load Deal Room deals');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchInvestors = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/investors?limit=1000`, { headers });
      setInvestors(res.data.items || []);
    } catch { /* silent — matches convention elsewhere */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);
  useEffect(() => { fetchInvestors(); }, [fetchInvestors]);

  const investorOptions = investors.map(i => ({
    id: i.id,
    name: `${i.first_name || ''} ${i.last_name || ''}`.trim() || i.entity_name || i.email || i.id,
    rp_code: i.risk_participant_code || '',
    email: i.email || '',
  }));
  // New allocations / capital calls: inactive investors (left the book) are not offered.
  const allocatableInvestorOptions = investorOptions.filter((_, idx) => investors[idx]?.portfolio_status !== 'inactive');

  const openDealDetail = async (dealId) => {
    setSelectedDealId(dealId);
    setDetailLoading(true);
    try {
      const [detailRes, distRes, eoiRes, callRes, collRes, updRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/deal-room/deals/${dealId}`, { headers }),
        axios.get(`${API_URL}/api/admin/deal-room/deals/${dealId}/distributions`, { headers }),
        axios.get(`${API_URL}/api/admin/deal-room/deals/${dealId}/eoi`, { headers }),
        axios.get(`${API_URL}/api/admin/deal-room/deals/${dealId}/capital-calls`, { headers }),
        axios.get(`${API_URL}/api/admin/deal-room/deals/${dealId}/collections`, { headers }),
        axios.get(`${API_URL}/api/admin/deal-room/deals/${dealId}/updates`, { headers }),
      ]);
      setSelectedDeal(detailRes.data.deal);
      setAllocations(detailRes.data.allocations || []);
      setDebtLeg(detailRes.data.debt_leg || null);
      setDistributions(distRes.data.distributions || []);
      setEois(eoiRes.data.eois || []);
      setCapitalCalls(callRes.data.capital_calls || []);
      setCollections(collRes.data.collections || []);
      setUpdates(updRes.data.updates || []);
    } catch (error) {
      toast.error('Failed to load deal detail');
      setSelectedDealId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = () => { if (selectedDealId) openDealDetail(selectedDealId); };

  const closeDetail = () => {
    setSelectedDealId(null);
    setSelectedDeal(null);
    setAllocations([]);
    setDebtLeg(null);
    setDistributions([]);
    setEois([]);
    setCapitalCalls([]);
    setCollections([]);
    setUpdates([]);
  };

  const handleCreateDeal = async () => {
    if (!newDeal.name.trim()) {
      toast.error('Deal name is required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...newDeal,
        target_amount: newDeal.target_amount === '' ? null : parseFloat(newDeal.target_amount),
        expected_collection_amount: newDeal.expected_collection_amount === '' ? null : parseFloat(newDeal.expected_collection_amount),
        indicative_return_low: newDeal.indicative_return_low === '' ? null : parseFloat(newDeal.indicative_return_low),
        indicative_return_high: newDeal.indicative_return_high === '' ? null : parseFloat(newDeal.indicative_return_high),
        expected_conclusion_date: newDeal.expected_conclusion_date || null,
        expected_settlement_date: newDeal.expected_settlement_date || null,
      };
      await axios.post(`${API_URL}/api/admin/deal-room/deals`, payload, { headers });
      toast.success('Deal created');
      setShowCreateDialog(false);
      setNewDeal(EMPTY_NEW_DEAL);
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deal');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenDeal = async (dealId) => {
    try {
      await axios.put(`${API_URL}/api/admin/deal-room/deals/${dealId}`, { status: 'open' }, { headers });
      toast.success('Deal opened for allocations');
      fetchDeals();
      if (selectedDealId === dealId) refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open deal');
    }
  };

  const handleReopenDeal = async (dealId) => {
    if (!window.confirm(
      'This sets the deal back to "open" so you can allocate/reverse investors. It stays open until you click '
      + '"Go live" again — nothing about existing allocations, distributions, or updates already posted changes.'
    )) return;
    await handleOpenDeal(dealId);
  };

  const openEditDialog = () => {
    if (!selectedDeal) return;
    setEditForm({
      name: selectedDeal.name || '',
      description: selectedDeal.description || '',
      counterparty: selectedDeal.counterparty || '',
      deal_type: selectedDeal.deal_type || '',
      target_amount: selectedDeal.target_amount ?? '',
      expected_collection_amount: selectedDeal.expected_collection_amount ?? '',
      expected_conclusion_date: selectedDeal.expected_conclusion_date || '',
      expected_settlement_date: selectedDeal.expected_settlement_date || '',
      indicative_return_low: selectedDeal.indicative_return_low ?? '',
      indicative_return_high: selectedDeal.indicative_return_high ?? '',
      indicative_return_note: selectedDeal.indicative_return_note || '',
      debt_loan_numbers: (selectedDeal.debt_loan_numbers || []).join(', '),
    });
    setShowEditDialog(true);
  };

  const handleUpdateDeal = async () => {
    if (!editForm.name.trim()) {
      toast.error('Deal name is required');
      return;
    }
    setEditing(true);
    try {
      const payload = {
        name: editForm.name,
        description: editForm.description || null,
        counterparty: editForm.counterparty || null,
        deal_type: editForm.deal_type || null,
        target_amount: editForm.target_amount === '' ? null : parseFloat(editForm.target_amount),
        expected_collection_amount: editForm.expected_collection_amount === '' ? null : parseFloat(editForm.expected_collection_amount),
        expected_conclusion_date: editForm.expected_conclusion_date || null,
        expected_settlement_date: editForm.expected_settlement_date || null,
        indicative_return_low: editForm.indicative_return_low === '' ? null : parseFloat(editForm.indicative_return_low),
        indicative_return_high: editForm.indicative_return_high === '' ? null : parseFloat(editForm.indicative_return_high),
        indicative_return_note: editForm.indicative_return_note || null,
        // Debt leg: loan-book loan numbers (all their tranches); empty clears it.
        debt_loan_numbers: (editForm.debt_loan_numbers || '').split(/[\s,;]+/).map(x => x.trim()).filter(Boolean),
      };
      await axios.put(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}`, payload, { headers });
      toast.success('Deal updated');
      setShowEditDialog(false);
      fetchDeals();
      refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update deal');
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteDeal = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}`, { headers });
      toast.success('Deal deleted');
      setDeleteConfirmOpen(false);
      closeDetail();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete deal');
    } finally {
      setDeleting(false);
    }
  };

  const handleGoLive = async () => {
    setGoingLive(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}/go-live`, {}, { headers });
      toast.success(res.data.funded_pct != null ? `Deal is live — ${res.data.funded_pct}% funded` : 'Deal is live');
      setGoLiveConfirmOpen(false);
      refreshDetail();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to go live');
    } finally {
      setGoingLive(false);
    }
  };

  const openDiagnostic = async () => {
    setShowDiagnosticDialog(true);
    setDiagnosticLoading(true);
    try {
      const [res, legRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/deal-room/maintenance/diagnose`, { headers }),
        axios.get(`${API_URL}/api/admin/deal-room/maintenance/diagnose-legacy-legs`, { headers }),
      ]);
      setDiagnostic(res.data);
      setLegacyLegs(legRes.data);
    } catch (error) {
      toast.error('Failed to run diagnostic');
    } finally {
      setDiagnosticLoading(false);
    }
  };

  const handleFixSigns = async () => {
    setFixingSigns(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/deal-room/maintenance/fix-legacy-signs`, {}, { headers });
      toast.success(`Fixed ${res.data.fixed} row(s)${res.data.skipped_already_reconciled.length ? `, skipped ${res.data.skipped_already_reconciled.length} already-reconciled row(s)` : ''}`);
      openDiagnostic();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix signs');
    } finally {
      setFixingSigns(false);
    }
  };

  const handleFixLegacyLegs = async () => {
    if (!window.confirm(
      `This will delete ${legacyLegs?.stale_legs_still_in_investor_transactions ?? 0} legacy deal_room_investment row(s) still lingering from the old design, and book ${legacyLegs?.allocations_missing_withdrawal_leg ?? 0} missing real cash-outflow row(s) (totalling ${formatCurrency(legacyLegs?.missing_withdrawal_total_amount ?? 0)}) for allocations that never got one. New rows land as "pending" — you'll still reconcile them normally against the bank. Continue?`
    )) return;
    setFixingLegacyLegs(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/deal-room/maintenance/fix-legacy-legs`, {}, { headers });
      toast.success(`Deleted ${res.data.legacy_investment_legs_deleted} legacy leg(s), booked ${res.data.withdrawal_legs_backfilled} missing withdrawal(s)`);
      openDiagnostic();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix legacy legs');
    } finally {
      setFixingLegacyLegs(false);
    }
  };

  const handleRecordCollection = async () => {
    if (!collectionForm.amount || parseFloat(collectionForm.amount) <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setCollecting(true);
    try {
      await axios.post(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}/collections`, {
        amount: parseFloat(collectionForm.amount),
        collection_date: collectionForm.collection_date || null,
        notes: collectionForm.notes || null,
      }, { headers });
      toast.success('Collection recorded');
      setShowCollectionDialog(false);
      setCollectionForm(EMPTY_COLLECTION);
      refreshDetail();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record collection');
    } finally {
      setCollecting(false);
    }
  };

  // Email the deal's investors with the update (2026-10-14).
  const [notifyInvestors, setNotifyInvestors] = useState(true);
  const [updateRecipients, setUpdateRecipients] = useState(null);
  useEffect(() => {
    if (!showUpdateDialog || !selectedDealId) return;
    setNotifyInvestors(true);
    setUpdateRecipients(null);
    axios.get(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}/update-recipients`, { headers })
      .then(r => setUpdateRecipients(r.data.recipients || []))
      .catch(() => setUpdateRecipients([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpdateDialog, selectedDealId]);

  const handlePostUpdate = async () => {
    if (!updateMsg.trim()) {
      toast.error('Enter an update message');
      return;
    }
    setPosting(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}/updates`,
        { message: updateMsg, notify_investors: notifyInvestors && (updateRecipients || []).length > 0 }, { headers });
      if (r.data.emailed) {
        toast.success(`Update posted and emailed to ${r.data.emailed_count} investor(s)`);
        if (r.data.email_failures) toast.warning(`${r.data.email_failures} email(s) could not be sent: ${r.data.emailed.filter(e => !e.sent).map(e => e.email).join(', ')}`);
      } else {
        toast.success('Update posted');
      }
      setShowUpdateDialog(false);
      setUpdateMsg(EMPTY_UPDATE_MSG);
      refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post update');
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    if (!window.confirm('Delete this update?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/deal-room/updates/${updateId}`, { headers });
      toast.success('Update deleted');
      refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete update');
    }
  };

  const handleAllocate = async () => {
    if (!allocForm.investor_id || !allocForm.amount || parseFloat(allocForm.amount) <= 0) {
      toast.error('Pick an investor and enter a positive amount');
      return;
    }
    if (allocForm.allocation_type === 'new_investment' && allocForm.funds_via_investor_account === null) {
      toast.error("Say whether this capital passes through the investor's own Investec account first");
      return;
    }
    setAllocating(true);
    try {
      const payload = {
        investor_id: allocForm.investor_id,
        allocation_type: allocForm.allocation_type,
        amount: parseFloat(allocForm.amount),
        allocation_date: allocForm.allocation_date || null,
        notes: allocForm.notes || null,
        capital_call_id: allocForm.capital_call_id || null,
        funds_via_investor_account: allocForm.allocation_type === 'new_investment' ? allocForm.funds_via_investor_account : null,
      };
      await axios.post(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}/allocations`, payload, { headers });
      toast.success('Investor allocated');
      setShowAllocateDialog(false);
      setAllocForm(EMPTY_ALLOCATION);
      refreshDetail();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate investor');
    } finally {
      setAllocating(false);
    }
  };

  const handleDeleteAllocation = async (allocationId) => {
    if (!window.confirm('Delete this reversed allocation? It is removed from the deal; the full record stays in the Audit Trail.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/deal-room/allocations/${allocationId}`, { headers });
      toast.success('Reversed allocation deleted');
      refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete allocation');
    }
  };

  const handleReverseAllocation = async (allocationId) => {
    if (!window.confirm('Reverse this allocation? This deletes the transaction(s) it created.')) return;
    try {
      await axios.post(`${API_URL}/api/admin/deal-room/allocations/${allocationId}/reverse`, {}, { headers });
      toast.success('Allocation reversed');
      refreshDetail();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse allocation');
    }
  };

  const handleReviewEoi = async (eoiId, action) => {
    try {
      await axios.post(`${API_URL}/api/admin/deal-room/eoi/${eoiId}/review`, { action }, { headers });
      toast.success(`EOI ${action}ed`);
      refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review EOI');
    }
  };

  const openAllocateForCall = (call) => {
    setAllocForm({
      investor_id: call.investor_id,
      allocation_type: 'new_investment',
      amount: String(call.amount),
      allocation_date: '',
      notes: '',
      capital_call_id: call.id,
    });
    setShowAllocateDialog(true);
  };

  const handleCreateCall = async () => {
    if (!callForm.investor_id || !callForm.amount || parseFloat(callForm.amount) <= 0) {
      toast.error('Pick an investor and enter a positive amount');
      return;
    }
    setCalling(true);
    try {
      const payload = {
        investor_id: callForm.investor_id,
        amount: parseFloat(callForm.amount),
        due_date: callForm.due_date || null,
        notes: callForm.notes || null,
      };
      const res = await axios.post(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}/capital-calls`, payload, { headers });
      toast.success(res.data.email_sent ? 'Capital called — investor notified by email' : 'Capital called — email could not be sent, follow up directly');
      setShowCallDialog(false);
      setCallForm(EMPTY_CALL);
      refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to call capital');
    } finally {
      setCalling(false);
    }
  };

  const handleCancelCall = async (callId) => {
    if (!window.confirm('Cancel this capital call?')) return;
    try {
      await axios.post(`${API_URL}/api/admin/deal-room/capital-calls/${callId}/cancel`, {}, { headers });
      toast.success('Capital call cancelled');
      refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel call');
    }
  };

  const openDistributeDialog = async () => {
    setShowDistributeDialog(true);
    setDistributeResults(null);
    setDistributeLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}/distribution-candidates`, { headers });
      setDistributeRows((res.data.candidates || []).map(c => ({
        ...c,
        include: true,
        amount: String(c.total_allocated),
        distribution_type: 'withdrawal',
        notes: '',
      })));
    } catch (error) {
      toast.error('Failed to load investors for this deal');
    } finally {
      setDistributeLoading(false);
    }
  };

  const updateDistributeRow = (investorId, patch) => {
    setDistributeRows(prev => prev.map(r => r.investor_id === investorId ? { ...r, ...patch } : r));
  };

  const handleDistribute = async () => {
    const items = distributeRows
      .filter(r => r.include && r.amount && parseFloat(r.amount) > 0)
      .map(r => ({
        investor_id: r.investor_id,
        amount: parseFloat(r.amount),
        distribution_type: r.distribution_type,
        notes: r.notes || null,
      }));
    if (items.length === 0) {
      toast.error('Include at least one investor with a positive amount');
      return;
    }
    setDistributing(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/deal-room/deals/${selectedDealId}/distributions/bulk`,
        { items, distribution_date: distributeDate || null },
        { headers },
      );
      setDistributeResults(res.data.results || []);
      toast.success(`${res.data.succeeded} of ${res.data.processed} distribution(s) booked`);
      refreshDetail();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record distributions');
    } finally {
      setDistributing(false);
    }
  };

  const handleConclude = async () => {
    setConcluding(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/deal-room/deals/${selectedDealId}/conclude`, {}, { headers });
      toast.success(`Deal concluded — actual return ${res.data.actual_return_pct ?? 'n/a'}%`);
      setConcludeConfirmOpen(false);
      refreshDetail();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to conclude deal');
    } finally {
      setConcluding(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Handshake className="h-6 w-6 text-indigo-600" /> Deal Room
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Off-loanbook deals — allocate investors, track returns separately from fund performance.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openDiagnostic} data-testid="deal-room-diagnostic-btn">
            <Stethoscope className="h-4 w-4 mr-1" /> Check data
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="deal-room-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New Deal
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Counterparty</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Funded</TableHead>
                <TableHead className="text-right">Distributed</TableHead>
                <TableHead>Return</TableHead>
                <TableHead>Opened</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Loading…</TableCell></TableRow>
              ) : deals.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No Deal Room deals yet</TableCell></TableRow>
              ) : deals.map(deal => {
                const cfg = STATUS_CONFIG[deal.status] || STATUS_CONFIG.draft;
                const pct = deal.target_amount ? Math.min((deal.total_allocated / deal.target_amount) * 100, 100) : null;
                return (
                  <TableRow
                    key={deal.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openDealDetail(deal.id)}
                    data-testid={`deal-room-row-${deal.id}`}
                  >
                    <TableCell className="font-medium">{deal.name}</TableCell>
                    <TableCell>{deal.counterparty || '-'}</TableCell>
                    <TableCell><Badge className={cfg.color}>{cfg.label}</Badge></TableCell>
                    <TableCell className="min-w-[160px]">
                      {deal.target_amount ? (
                        <div className="space-y-1">
                          <Progress value={pct || 0} className="h-1.5" />
                          <p className="text-xs text-slate-500 font-mono">{formatCurrency(deal.total_allocated)} / {formatCurrency(deal.target_amount)}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 font-mono">{formatCurrency(deal.total_allocated)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(deal.total_distributed)}</TableCell>
                    <TableCell className="text-sm">
                      {deal.status === 'concluded'
                        ? <span className="font-medium text-blue-700">{deal.actual_return_pct}% actual</span>
                        : (deal.indicative_return_low != null
                          ? <span className="text-slate-500">{deal.indicative_return_low}–{deal.indicative_return_high}% indic.</span>
                          : '-')}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(deal.opened_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create deal dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Deal Room deal</DialogTitle>
            <DialogDescription>Kept entirely separate from the loan book — for temporary, outside-investor opportunities.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Name *</Label>
              <Input value={newDeal.name} onChange={e => setNewDeal(p => ({ ...p, name: e.target.value }))} data-testid="deal-room-name" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea value={newDeal.description} onChange={e => setNewDeal(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Counterparty</Label>
                <Input value={newDeal.counterparty} onChange={e => setNewDeal(p => ({ ...p, counterparty: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Deal type</Label>
                <Input value={newDeal.deal_type} onChange={e => setNewDeal(p => ({ ...p, deal_type: e.target.value }))} placeholder="e.g. bridge_loan" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Target raise (ZAR)</Label>
                <Input type="number" step="0.01" min="0" value={newDeal.target_amount} onChange={e => setNewDeal(p => ({ ...p, target_amount: e.target.value }))} data-testid="deal-room-target" />
              </div>
              <div className="space-y-1">
                <Label>Expected collection (ZAR)</Label>
                <Input type="number" step="0.01" min="0" value={newDeal.expected_collection_amount} onChange={e => setNewDeal(p => ({ ...p, expected_collection_amount: e.target.value }))} placeholder="Principal + return expected back" data-testid="deal-room-expected-collection" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Expected conclusion date</Label>
                <Input type="date" value={newDeal.expected_conclusion_date} onChange={e => setNewDeal(p => ({ ...p, expected_conclusion_date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Expected settlement date</Label>
                <Input type="date" value={newDeal.expected_settlement_date} onChange={e => setNewDeal(p => ({ ...p, expected_settlement_date: e.target.value }))} title="When investor distributions are expected to be paid" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Indicative return — low %</Label>
                <Input type="number" step="0.1" value={newDeal.indicative_return_low} onChange={e => setNewDeal(p => ({ ...p, indicative_return_low: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Indicative return — high %</Label>
                <Input type="number" step="0.1" value={newDeal.indicative_return_high} onChange={e => setNewDeal(p => ({ ...p, indicative_return_high: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Indicative return note</Label>
              <Input value={newDeal.indicative_return_note} onChange={e => setNewDeal(p => ({ ...p, indicative_return_note: e.target.value }))} placeholder="e.g. target 18-22% p.a., subject to deal terms" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDeal} disabled={creating} data-testid="deal-room-create-confirm">
              {creating ? 'Creating…' : 'Create deal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deal detail dialog */}
      <Dialog open={!!selectedDealId} onOpenChange={(v) => { if (!v) closeDetail(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="deal-room-detail-dialog">
          {detailLoading || !selectedDeal ? (
            <div className="py-12 text-center text-slate-400">Loading…</div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedDeal.name}
                  <Badge className={(STATUS_CONFIG[selectedDeal.status] || STATUS_CONFIG.draft).color}>
                    {(STATUS_CONFIG[selectedDeal.status] || STATUS_CONFIG.draft).label}
                  </Badge>
                </DialogTitle>
                <DialogDescription>{selectedDeal.description || 'No description'}</DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-6 pr-1">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 border rounded-lg p-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Counterparty</p>
                    <p className="font-medium">{selectedDeal.counterparty || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Funded</p>
                    {selectedDeal.target_amount ? (
                      <>
                        <Progress value={Math.min((selectedDeal.total_allocated / selectedDeal.target_amount) * 100, 100)} className="h-1.5 my-1" />
                        <p className="font-mono text-sm">{formatCurrency(selectedDeal.total_allocated)} / {formatCurrency(selectedDeal.target_amount)}</p>
                      </>
                    ) : (
                      <p className="font-mono font-medium">{formatCurrency(selectedDeal.total_allocated)}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Collected</p>
                    {(() => {
                      // Deal Room collections (entered by hand) + debt repaid from the
                      // loan book (automatic); target includes the debt to collect.
                      const cs = selectedDeal.collections_summary || {};
                      const total = cs.collected_total ?? selectedDeal.total_collected;
                      return cs.expected_total ? (
                        <>
                          <Progress value={Math.min(cs.progress_pct || 0, 100)} className="h-1.5 my-1" />
                          <p className="font-mono text-sm">{formatCurrency(total)} / {formatCurrency(cs.expected_total)}</p>
                          {cs.expected_debt > 0 && (
                            <p className="text-[11px] text-slate-500" data-testid="deal-room-collected-split">
                              Debt {formatCurrency(cs.collected_debt)} / {formatCurrency(cs.expected_debt)} (loan book) · Deal Room {formatCurrency(cs.collected_manual)}{cs.expected_manual != null ? ` / ${formatCurrency(cs.expected_manual)}` : ''} (entered)
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="font-mono font-medium">{formatCurrency(total)}</p>
                      );
                    })()}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Distributed</p>
                    <p className="font-mono font-medium">{formatCurrency(selectedDeal.total_distributed)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Return</p>
                    <p className="font-medium">
                      {selectedDeal.status === 'concluded'
                        ? `${selectedDeal.actual_return_pct}% actual (simple)`
                        : (selectedDeal.indicative_return_low != null
                          ? `${selectedDeal.indicative_return_low}–${selectedDeal.indicative_return_high}% indicative`
                          : 'Not set')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Expected conclusion</p>
                    <p className="font-medium text-sm">{formatDate(selectedDeal.expected_conclusion_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Expected settlement</p>
                    <p className="font-medium text-sm">{formatDate(selectedDeal.expected_settlement_date)}</p>
                  </div>
                </div>
                {selectedDeal.indicative_return_note && (
                  <p className="text-xs text-slate-500 italic -mt-3">Note: {selectedDeal.indicative_return_note}</p>
                )}

                {debtLeg && (
                  <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="deal-room-debt-leg">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-medium text-sm">Debt leg · loans {debtLeg.loan_numbers.join(', ')}</p>
                      <p className="text-sm">Total deal value <strong>{formatCurrency(selectedDeal.total_deal_value)}</strong>
                        <span className="text-xs text-slate-500"> (debt {formatCurrency(debtLeg.drawn)} + Deal Room {formatCurrency(selectedDeal.total_allocated)})</span></p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div><p className="text-xs text-slate-500">Drawn</p><p className="font-mono">{formatCurrency(debtLeg.drawn)}</p></div>
                      <div><p className="text-xs text-slate-500">Repaid</p><p className="font-mono text-emerald-700">{formatCurrency(debtLeg.repaid)}</p></div>
                      <div><p className="text-xs text-slate-500">Outstanding</p><p className="font-mono">{formatCurrency(debtLeg.outstanding)}</p></div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(debtLeg.settled_pct, 100)}%` }} /></div>
                    <p className="text-xs text-slate-500">{debtLeg.fully_settled ? `Debt fully settled on ${debtLeg.settled_on}; the Deal Room portion settles next.` : `${debtLeg.settled_pct}% of the debt settled (from the loan book). The debt is settled before the Deal Room portion.`}</p>
                    <details className="text-xs"><summary className="cursor-pointer text-slate-600">Per tranche (drawn · repaid · outstanding)</summary>
                      <table className="w-full mt-1"><tbody>
                        {debtLeg.loans.map(l => (
                          <tr key={l.loan_id} className="border-t"><td className="py-1 font-mono">{l.loan_id}</td><td className="text-right font-mono">{formatCurrency(l.drawn)}</td><td className="text-right font-mono text-emerald-700">{formatCurrency(l.repaid)}</td><td className="text-right font-mono">{formatCurrency(l.outstanding)}</td></tr>
                        ))}
                      </tbody></table>
                    </details>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={openEditDialog} data-testid="deal-room-edit-btn">
                    <Pencil className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  {allocations.every(a => a.status !== 'booked') && (
                    <Button size="sm" variant="outline" className="text-red-700 border-red-300" onClick={() => setDeleteConfirmOpen(true)} data-testid="deal-room-delete-btn">
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  )}
                  {selectedDeal.status === 'draft' && (
                    <Button size="sm" variant="outline" onClick={() => handleOpenDeal(selectedDeal.id)} data-testid="deal-room-open-btn">
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Open for allocations
                    </Button>
                  )}
                  {(selectedDeal.status === 'live' || selectedDeal.status === 'closed_to_new_allocations') && (
                    <Button size="sm" variant="outline" onClick={() => handleReopenDeal(selectedDeal.id)} data-testid="deal-room-reopen-btn">
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Reopen for allocations
                    </Button>
                  )}
                  {(selectedDeal.status === 'draft' || selectedDeal.status === 'open') && (
                    <>
                      <Button size="sm" onClick={() => setShowAllocateDialog(true)} data-testid="deal-room-allocate-btn">
                        <ArrowUpCircle className="h-4 w-4 mr-1" /> Allocate investor
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowCallDialog(true)} data-testid="deal-room-call-btn">
                        <PhoneCall className="h-4 w-4 mr-1" /> Call capital
                      </Button>
                    </>
                  )}
                  {(selectedDeal.status === 'open' || selectedDeal.status === 'closed_to_new_allocations') && (
                    <Button size="sm" variant="outline" className="text-indigo-700 border-indigo-300" onClick={() => setGoLiveConfirmOpen(true)} data-testid="deal-room-golive-btn">
                      <Radio className="h-4 w-4 mr-1" /> Go live
                    </Button>
                  )}
                  {(selectedDeal.status === 'open' || selectedDeal.status === 'closed_to_new_allocations' || selectedDeal.status === 'live') && (
                    <Button size="sm" variant="outline" onClick={() => setShowCollectionDialog(true)} data-testid="deal-room-collect-btn">
                      <Coins className="h-4 w-4 mr-1" /> Record collection
                    </Button>
                  )}
                  {selectedDeal.status !== 'draft' && (
                    <Button size="sm" variant="outline" onClick={() => setShowUpdateDialog(true)} data-testid="deal-room-post-update-btn">
                      <Megaphone className="h-4 w-4 mr-1" /> Post update
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={openDistributeDialog} data-testid="deal-room-distribute-btn">
                    <ArrowDownCircle className="h-4 w-4 mr-1" /> Record distribution
                  </Button>
                  {selectedDeal.status !== 'concluded' && selectedDeal.status !== 'written_off' && (
                    <Button size="sm" variant="outline" className="ml-auto text-blue-700 border-blue-300" onClick={() => setConcludeConfirmOpen(true)} data-testid="deal-room-conclude-btn">
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Conclude deal
                    </Button>
                  )}
                </div>

                {/* Progress updates */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Progress updates (visible to investors)</h3>
                  {updates.length === 0 ? (
                    <p className="text-sm text-slate-400 py-2">No updates posted yet</p>
                  ) : (
                    <div className="space-y-2">
                      {updates.map(u => (
                        <div key={u.id} className="flex items-start justify-between gap-2 bg-slate-50 border rounded-lg p-3" data-testid={`deal-room-update-${u.id}`}>
                          <div>
                            <p className="text-sm text-slate-800">{u.message}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {formatDate(u.posted_at)}
                              {u.emailed_count != null && ` · emailed to ${u.emailed_count} investor(s)${u.email_failures ? `, ${u.email_failures} failed` : ''}`}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 flex-shrink-0" onClick={() => handleDeleteUpdate(u.id)} title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Collections */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Collections</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {collections.length === 0 ? (
                        <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-6">No collections recorded yet</TableCell></TableRow>
                      ) : collections.map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="text-right font-mono">{formatCurrency(c.amount)}</TableCell>
                          <TableCell className="text-sm">{formatDate(c.collection_date)}</TableCell>
                          <TableCell className="text-sm text-slate-500">{c.notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Expressions of interest */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Expressions of interest</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Investor</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-40"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eois.length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">No expressions of interest yet</TableCell></TableRow>
                      ) : eois.map(e => (
                        <TableRow key={e.id} data-testid={`deal-room-eoi-${e.id}`}>
                          <TableCell>{e.investor_name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(e.amount)}</TableCell>
                          <TableCell>
                            {e.status === 'submitted' && <Badge className="bg-blue-100 text-blue-700 text-xs">Submitted</Badge>}
                            {e.status === 'accepted' && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Accepted</Badge>}
                            {e.status === 'declined' && <Badge variant="destructive" className="text-xs">Declined</Badge>}
                            {e.status === 'withdrawn' && <Badge className="bg-slate-100 text-slate-500 text-xs">Withdrawn</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            {e.status === 'submitted' && (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700" onClick={() => handleReviewEoi(e.id, 'accept')}>Accept</Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => handleReviewEoi(e.id, 'decline')}>Decline</Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Capital calls */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Capital calls</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Investor</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Due</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-40"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {capitalCalls.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No capital calls yet</TableCell></TableRow>
                      ) : capitalCalls.map(c => (
                        <TableRow key={c.id} data-testid={`deal-room-call-${c.id}`}>
                          <TableCell>{c.investor_name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(c.amount)}</TableCell>
                          <TableCell className="text-sm">{formatDate(c.due_date)}</TableCell>
                          <TableCell>
                            {c.status === 'called' && (
                              <div className="flex items-center gap-1">
                                <Badge className="bg-amber-100 text-amber-700 text-xs">Called</Badge>
                                {c.email_sent
                                  ? <Mail className="h-3.5 w-3.5 text-emerald-600" titleAccess="Email sent" />
                                  : <XCircle className="h-3.5 w-3.5 text-red-500" titleAccess="Email not sent" />}
                              </div>
                            )}
                            {c.status === 'fulfilled' && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Fulfilled</Badge>}
                            {c.status === 'cancelled' && <Badge className="bg-slate-100 text-slate-500 text-xs">Cancelled</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            {c.status === 'called' && (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openAllocateForCall(c)}>Fulfill</Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => handleCancelCall(c.id)}>Cancel</Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Allocations */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Allocations</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Investor</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allocations.length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No allocations yet</TableCell></TableRow>
                      ) : allocations.map(a => (
                        <TableRow key={a.id} data-testid={`deal-room-alloc-${a.id}`}>
                          <TableCell>{a.investor_name}</TableCell>
                          <TableCell className="text-sm">{ALLOCATION_TYPE_LABEL[a.allocation_type] || a.allocation_type}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(a.amount)}</TableCell>
                          <TableCell className="text-sm">{formatDate(a.allocation_date)}</TableCell>
                          <TableCell>
                            {a.status === 'reversed'
                              ? <Badge variant="destructive" className="text-xs"><Ban className="h-3 w-3 mr-1" />Reversed</Badge>
                              : <Badge className="bg-emerald-100 text-emerald-700 text-xs">Booked</Badge>}
                          </TableCell>
                          <TableCell>
                            {a.status === 'booked' && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleReverseAllocation(a.id)} title="Reverse">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {a.status === 'reversed' && (
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 text-xs" onClick={() => handleDeleteAllocation(a.id)} title="Delete this reversed allocation" data-testid={`deal-room-alloc-delete-${a.id}`}>
                                Delete
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Distributions */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Distributions</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Investor</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Reconciliation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {distributions.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No distributions yet</TableCell></TableRow>
                      ) : distributions.map(d => (
                        <TableRow key={d.id}>
                          <TableCell>{d.investor_name}</TableCell>
                          <TableCell className="text-sm">
                            <Badge className={d.distribution_type === 'reinvestment' ? 'bg-indigo-100 text-indigo-700 text-xs' : 'bg-slate-100 text-slate-700 text-xs'}>
                              {DISTRIBUTION_TYPE_LABEL[d.distribution_type] || 'Withdrawal'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(d.amount)}</TableCell>
                          <TableCell className="text-sm">{formatDate(d.transaction_date)}</TableCell>
                          <TableCell className="text-xs">
                            {d.reconciliation_status === 'verified_no_match'
                              ? <span className="text-slate-500">No bank movement</span>
                              : <span className="text-amber-600">Pending bank match</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Allocate investor dialog */}
      <Dialog open={showAllocateDialog} onOpenChange={(v) => { setShowAllocateDialog(v); if (!v) setAllocForm(EMPTY_ALLOCATION); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Allocate investor</DialogTitle>
            <DialogDescription>
              {allocForm.capital_call_id
                ? 'Fulfilling a capital call — investor and amount are pre-filled from the call.'
                : '"Fund reallocation" books a real outflow from the fund\'s account (paid out for this deal) — it needs normal bank reconciliation, same as a withdrawal. "New investment" expects a real incoming bank transaction to reconcile against instead.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Investor *</Label>
              <InvestorPicker
                value={allocForm.investor_id}
                onChange={(id) => setAllocForm(p => ({ ...p, investor_id: id }))}
                investors={allocatableInvestorOptions}
                includeAll={false}
                placeholder="Select investor…"
                className="w-full"
                testId="deal-room-alloc-investor"
              />
            </div>
            <div className="space-y-1">
              <Label>Allocation type *</Label>
              <Select value={allocForm.allocation_type} onValueChange={v => setAllocForm(p => ({ ...p, allocation_type: v, funds_via_investor_account: null }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_investment">New investment (fresh capital)</SelectItem>
                  <SelectItem value="fund_reallocation">Fund reallocation (existing fund capital)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {allocForm.allocation_type === 'new_investment' && (
              <div className="space-y-1">
                <Label>Does this capital pass through the investor's own Investec account first? *</Label>
                <Select
                  value={allocForm.funds_via_investor_account === null ? '' : String(allocForm.funds_via_investor_account)}
                  onValueChange={v => setAllocForm(p => ({ ...p, funds_via_investor_account: v === 'true' }))}
                >
                  <SelectTrigger data-testid="deal-room-alloc-funds-via-account"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes — passes through the investor's Investec account</SelectItem>
                    <SelectItem value="false">No — goes straight into the deal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <p className="text-xs text-slate-500">
              Every allocation books one pending Deal Room Withdrawal on the investor's ledger for this amount.
              Match it to the bank outflow in Reconciliation, or Verify (no match) if no bank movement exists.
              The stake itself is never a ledger entry — it's tracked on this allocation.
            </p>
            <div className="space-y-1">
              <Label>Amount (ZAR) *</Label>
              <Input type="number" step="0.01" min="0" value={allocForm.amount} onChange={e => setAllocForm(p => ({ ...p, amount: e.target.value }))} data-testid="deal-room-alloc-amount" />
            </div>
            <div className="space-y-1">
              <Label>Allocation date</Label>
              <Input type="date" value={allocForm.allocation_date} onChange={e => setAllocForm(p => ({ ...p, allocation_date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea value={allocForm.notes} onChange={e => setAllocForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAllocateDialog(false); setAllocForm(EMPTY_ALLOCATION); }}>Cancel</Button>
            <Button onClick={handleAllocate} disabled={allocating} data-testid="deal-room-allocate-confirm">
              {allocating ? 'Allocating…' : 'Allocate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Distribution dialog */}
      <Dialog open={showDistributeDialog} onOpenChange={setShowDistributeDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="deal-room-distribute-dialog">
          <DialogHeader>
            <DialogTitle>Distribute to investors</DialogTitle>
            <DialogDescription>
              <strong>Withdrawal</strong> books a pending distribution for ops to pay out manually and reconcile later — no automated transfer.
              <strong> Reinvestment</strong> fires a real Investec transfer into the investor's linked account right now (same mechanism as repayment payouts) —
              only available for investors with one.
            </DialogDescription>
          </DialogHeader>

          {distributeLoading ? (
            <div className="py-12 text-center text-slate-400">Loading investors…</div>
          ) : (
            <>
              <div className="space-y-1">
                <Label>Distribution date</Label>
                <Input type="date" className="w-48" value={distributeDate} onChange={e => setDistributeDate(e.target.value)} />
              </div>

              <div className="flex-1 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Investor</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {distributeRows.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No allocated investors on this deal</TableCell></TableRow>
                    ) : distributeRows.map(r => {
                      const result = distributeResults?.find(x => x.investor_id === r.investor_id);
                      return (
                        <TableRow key={r.investor_id} data-testid={`deal-room-dist-row-${r.investor_id}`}>
                          <TableCell>
                            <input type="checkbox" checked={r.include} onChange={e => updateDistributeRow(r.investor_id, { include: e.target.checked })} />
                          </TableCell>
                          <TableCell className="text-sm">{r.investor_name}</TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number" step="0.01" min="0" className="w-32 h-8 text-right font-mono ml-auto"
                              value={r.amount} onChange={e => updateDistributeRow(r.investor_id, { amount: e.target.value })}
                              data-testid={`deal-room-dist-amount-${r.investor_id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Select value={r.distribution_type} onValueChange={v => updateDistributeRow(r.investor_id, { distribution_type: v })}>
                              <SelectTrigger className="h-8 w-40" data-testid={`deal-room-dist-type-${r.investor_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="withdrawal">Withdrawal</SelectItem>
                                <SelectItem value="reinvestment" disabled={!r.has_investec_mapping}>
                                  Reinvestment{!r.has_investec_mapping ? ' (no linked account)' : ''}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input className="h-8 w-36" value={r.notes} onChange={e => updateDistributeRow(r.investor_id, { notes: e.target.value })} />
                          </TableCell>
                          <TableCell className="text-xs">
                            {result && (result.ok
                              ? <span className="text-emerald-600">Booked{result.transfer_result ? ` — ${result.transfer_result.status}` : ''}</span>
                              : <span className="text-red-600">{result.error}</span>)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDistributeDialog(false)}>Close</Button>
            <Button onClick={handleDistribute} disabled={distributing || distributeLoading} data-testid="deal-room-distribute-confirm">
              {distributing ? 'Processing…' : 'Process distributions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Go live confirm */}
      <Dialog open={goLiveConfirmOpen} onOpenChange={setGoLiveConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Make this deal live?</DialogTitle>
            <DialogDescription>
              Investors will see it as live and can follow progress updates you post. You can do this
              even if it's not fully funded yet — that's your call.
            </DialogDescription>
          </DialogHeader>
          {selectedDeal && (
            <div className="text-sm space-y-1 bg-slate-50 border rounded p-3">
              <div className="flex justify-between"><span>Funded</span>
                <span className="font-mono">
                  {formatCurrency(selectedDeal.total_allocated)}{selectedDeal.target_amount ? ` / ${formatCurrency(selectedDeal.target_amount)}` : ''}
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoLiveConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleGoLive} disabled={goingLive} className="bg-indigo-600 hover:bg-indigo-700" data-testid="deal-room-golive-confirm">
              {goingLive ? 'Going live…' : 'Go live'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record collection dialog */}
      <Dialog open={showCollectionDialog} onOpenChange={(v) => { setShowCollectionDialog(v); if (!v) setCollectionForm(EMPTY_COLLECTION); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record collection</DialogTitle>
            <DialogDescription>Money collected in from the deal itself (e.g. a repayment), before it's distributed to investors.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Amount (ZAR) *</Label>
              <Input type="number" step="0.01" min="0" value={collectionForm.amount} onChange={e => setCollectionForm(p => ({ ...p, amount: e.target.value }))} data-testid="deal-room-collection-amount" />
            </div>
            <div className="space-y-1">
              <Label>Collection date</Label>
              <Input type="date" value={collectionForm.collection_date} onChange={e => setCollectionForm(p => ({ ...p, collection_date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea value={collectionForm.notes} onChange={e => setCollectionForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCollectionDialog(false); setCollectionForm(EMPTY_COLLECTION); }}>Cancel</Button>
            <Button onClick={handleRecordCollection} disabled={collecting} data-testid="deal-room-collection-confirm">
              {collecting ? 'Recording…' : 'Record collection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post update dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={(v) => { setShowUpdateDialog(v); if (!v) setUpdateMsg(EMPTY_UPDATE_MSG); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Post a progress update</DialogTitle>
            <DialogDescription>Visible to investors browsing this deal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Message *</Label>
              <Textarea value={updateMsg} onChange={e => setUpdateMsg(e.target.value)} rows={4} data-testid="deal-room-update-message" />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={notifyInvestors} disabled={!(updateRecipients || []).length}
                onChange={e => setNotifyInvestors(e.target.checked)} data-testid="deal-room-update-notify" />
              <span>
                Email this update to the investors in this deal
                {updateRecipients == null ? ' …' : ` (${updateRecipients.length})`}
                {updateRecipients && updateRecipients.length > 0 && (
                  <details className="text-xs text-slate-500"><summary className="cursor-pointer">Who will receive it</summary>
                    {updateRecipients.map(r => <div key={r.email}>{r.rp_code ? `${r.rp_code} · ` : ''}{r.name} — {r.email}</div>)}
                  </details>
                )}
                {updateRecipients && updateRecipients.length === 0 && <span className="block text-xs text-slate-500">No investors with an active allocation yet.</span>}
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUpdateDialog(false); setUpdateMsg(EMPTY_UPDATE_MSG); }}>Cancel</Button>
            <Button onClick={handlePostUpdate} disabled={posting} data-testid="deal-room-update-confirm">
              {posting ? 'Posting…' : 'Post update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data diagnostic dialog */}
      <Dialog open={showDiagnosticDialog} onOpenChange={setShowDiagnosticDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="deal-room-diagnostic-dialog">
          <DialogHeader>
            <DialogTitle>Deal Room data check</DialogTitle>
            <DialogDescription>
              Checks for the legacy withdrawal-sign bug (fixed 2026-09-29) and for genuine duplicate transactions
              (two rows sharing the same allocation AND the same type — NOT a fund reallocation's normal two-row
              pair, which is one withdrawal + one investment row by design).
            </DialogDescription>
          </DialogHeader>
          {diagnosticLoading || !diagnostic ? (
            <div className="py-12 text-center text-slate-400">Checking…</div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Wrong-sign withdrawals ({diagnostic.wrong_sign_count})
                  </h3>
                  {diagnostic.wrong_sign_count > 0 && (
                    <Button size="sm" onClick={handleFixSigns} disabled={fixingSigns} data-testid="deal-room-fix-signs-btn">
                      {fixingSigns ? 'Fixing…' : 'Fix signs'}
                    </Button>
                  )}
                </div>
                {diagnostic.wrong_sign_count === 0 ? (
                  <p className="text-sm text-slate-400">None found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Investor</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Reconciliation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {diagnostic.wrong_sign_rows.map(r => (
                        <TableRow key={r.id}>
                          <TableCell className="text-sm">{r.investor_name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(r.amount)}</TableCell>
                          <TableCell className="text-sm">{formatDate(r.transaction_date)}</TableCell>
                          <TableCell className="text-xs">
                            {r.matched_bank_txn_id || (r.reconciliation_status && r.reconciliation_status !== 'pending')
                              ? <span className="text-amber-600">Already reconciled — needs manual review, not auto-fixed</span>
                              : <span className="text-slate-500">Pending — will be fixed</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">
                  Genuine duplicate groups ({diagnostic.genuine_duplicate_groups.length})
                </h3>
                {diagnostic.genuine_duplicate_groups.length === 0 ? (
                  <p className="text-sm text-slate-400">None found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Investor</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {diagnostic.genuine_duplicate_groups.map((d, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">{d.investor_name}</TableCell>
                          <TableCell className="text-sm">{d.transaction_type}</TableCell>
                          <TableCell className="text-right">{d.count}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(d.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {legacyLegs && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-700">
                      Legacy "mirror the stake" leftovers (2026-09-30 redesign)
                    </h3>
                    {(legacyLegs.stale_legs_still_in_investor_transactions > 0 || legacyLegs.allocations_missing_withdrawal_leg > 0) && (
                      <Button size="sm" onClick={handleFixLegacyLegs} disabled={fixingLegacyLegs} data-testid="deal-room-fix-legacy-legs-btn">
                        {fixingLegacyLegs ? 'Fixing…' : 'Fix now'}
                      </Button>
                    )}
                  </div>
                  {legacyLegs.stale_legs_still_in_investor_transactions === 0 && legacyLegs.allocations_missing_withdrawal_leg === 0 ? (
                    <p className="text-sm text-slate-400">None found</p>
                  ) : (
                    <ul className="text-sm text-slate-600 space-y-1 list-disc pl-5">
                      <li>
                        {legacyLegs.stale_legs_still_in_investor_transactions} stale deal_room_investment row(s) still
                        booked from before every allocation type carried its own real cash-outflow row — will be deleted.
                      </li>
                      <li>
                        {legacyLegs.allocations_missing_withdrawal_leg} allocation(s) — totalling{' '}
                        {formatCurrency(legacyLegs.missing_withdrawal_total_amount)} — never got a real
                        deal_room_withdrawal row at all; will be booked now (pending reconciliation, same as any
                        new allocation).
                      </li>
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiagnosticDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call capital dialog */}
      <Dialog open={showCallDialog} onOpenChange={(v) => { setShowCallDialog(v); if (!v) setCallForm(EMPTY_CALL); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Call capital</DialogTitle>
            <DialogDescription>Notifies the investor by email. Doesn't move any money — once they pay, fulfil the call from the Capital Calls list.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Investor *</Label>
              <InvestorPicker
                value={callForm.investor_id}
                onChange={(id) => setCallForm(p => ({ ...p, investor_id: id }))}
                investors={allocatableInvestorOptions}
                includeAll={false}
                placeholder="Select investor…"
                className="w-full"
                testId="deal-room-call-investor"
              />
            </div>
            <div className="space-y-1">
              <Label>Amount (ZAR) *</Label>
              <Input type="number" step="0.01" min="0" value={callForm.amount} onChange={e => setCallForm(p => ({ ...p, amount: e.target.value }))} data-testid="deal-room-call-amount" />
            </div>
            <div className="space-y-1">
              <Label>Due date</Label>
              <Input type="date" value={callForm.due_date} onChange={e => setCallForm(p => ({ ...p, due_date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Notes (included in the email)</Label>
              <Textarea value={callForm.notes} onChange={e => setCallForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCallDialog(false); setCallForm(EMPTY_CALL); }}>Cancel</Button>
            <Button onClick={handleCreateCall} disabled={calling} data-testid="deal-room-call-confirm">
              {calling ? 'Calling…' : 'Call capital'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conclude confirm */}
      <Dialog open={concludeConfirmOpen} onOpenChange={setConcludeConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Conclude this deal?</DialogTitle>
            <DialogDescription>
              This locks the deal and computes its actual return as a simple total return:
              (distributed − allocated) / allocated. This cannot be undone from here.
            </DialogDescription>
          </DialogHeader>
          {selectedDeal && (
            <div className="text-sm space-y-1 bg-slate-50 border rounded p-3">
              <div className="flex justify-between"><span>Total allocated</span><span className="font-mono">{formatCurrency(selectedDeal.total_allocated)}</span></div>
              <div className="flex justify-between"><span>Total distributed</span><span className="font-mono">{formatCurrency(selectedDeal.total_distributed)}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConcludeConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleConclude} disabled={concluding} className="bg-blue-600 hover:bg-blue-700" data-testid="deal-room-conclude-confirm">
              {concluding ? 'Concluding…' : 'Conclude deal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit deal dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit deal</DialogTitle>
            <DialogDescription>Status changes (open / go-live / conclude / write-off) stay on their own buttons — this only edits the deal's details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Name *</Label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="deal-room-edit-name" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Counterparty</Label>
                <Input value={editForm.counterparty} onChange={e => setEditForm(p => ({ ...p, counterparty: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Deal type</Label>
                <Input value={editForm.deal_type} onChange={e => setEditForm(p => ({ ...p, deal_type: e.target.value }))} placeholder="e.g. bridge_loan" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Target raise (ZAR)</Label>
                <Input type="number" step="0.01" min="0" value={editForm.target_amount} onChange={e => setEditForm(p => ({ ...p, target_amount: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Expected collection (ZAR)</Label>
                <Input type="number" step="0.01" min="0" value={editForm.expected_collection_amount} onChange={e => setEditForm(p => ({ ...p, expected_collection_amount: e.target.value }))} placeholder="Principal + return expected back" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Expected conclusion date</Label>
                <Input type="date" value={editForm.expected_conclusion_date} onChange={e => setEditForm(p => ({ ...p, expected_conclusion_date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Expected settlement date</Label>
                <Input type="date" value={editForm.expected_settlement_date} onChange={e => setEditForm(p => ({ ...p, expected_settlement_date: e.target.value }))} data-testid="deal-room-edit-settlement-date" title="When investor distributions are expected to be paid" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Indicative return — low %</Label>
                <Input type="number" step="0.1" value={editForm.indicative_return_low} onChange={e => setEditForm(p => ({ ...p, indicative_return_low: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Indicative return — high %</Label>
                <Input type="number" step="0.1" value={editForm.indicative_return_high} onChange={e => setEditForm(p => ({ ...p, indicative_return_high: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Indicative return note</Label>
              <Input value={editForm.indicative_return_note} onChange={e => setEditForm(p => ({ ...p, indicative_return_note: e.target.value }))} placeholder="e.g. target 18-22% p.a., subject to deal terms" />
            </div>
            <div className="space-y-1">
              <Label>Debt leg — loan-book loan numbers (optional)</Label>
              <Input value={editForm.debt_loan_numbers || ''} onChange={e => setEditForm(p => ({ ...p, debt_loan_numbers: e.target.value }))}
                placeholder="e.g. 002743, 002744 — all tranches of each loan" data-testid="deal-room-edit-debt-loans" />
              <p className="text-xs text-slate-500">Only for deals where the Deal Room portion is a quasi-equity tranche. The debt (amount drawn on these loans) is added to the total deal value and shown as settled first, from the loan book's repayments. Investors see amounts and progress, never loan numbers.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateDeal} disabled={editing} data-testid="deal-room-edit-confirm">
              {editing ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete deal confirm */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this deal?</DialogTitle>
            <DialogDescription>
              This permanently removes the deal and its EOIs, capital calls, updates and collections.
              It's only allowed while no investor allocation is active; reversed allocations are deleted with it.
              Everything removed is kept in the Audit Trail.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteDeal} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="deal-room-delete-confirm">
              {deleting ? 'Deleting…' : 'Delete deal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
